// WebSocket Service for PWA
import { io } from 'socket.io-client';
import { configService } from './config.js';

export class WebSocketService {
  constructor() {
    this.socket = null;
    this.currentSessionId = null;
    this.currentParticipantId = null;
    this.isConnecting = false;
    this.connectionAttempts = 0;
    this.maxConnectionAttempts = 3;
    this._speakingOffTimer = null;
    this.serverConfig = null; // populated from participant_joined response

    // Listen for backend changes
    window.addEventListener('backendChanged', (event) => {
      console.log('Backend changed, WebSocket service reconnecting:', event.detail);
      this.disconnect();
    });
  }

  async connect(serverUrl = null) {
    // Use dynamic server URL if not provided
    if (!serverUrl) {
      serverUrl = configService.getWebSocketUrl();
    }

    return new Promise((resolve, reject) => {
      if (this.isConnecting) {
        console.log('Connection attempt already in progress');
        return;
      }

      if (this.socket && this.socket.connected) {
        console.log('WebSocket already connected, reusing connection');
        resolve();
        return;
      }
      
      this.isConnecting = true;
      this.connectionAttempts++;
      
      if (this.socket) {
        console.log('Cleaning up existing disconnected socket');
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }
      
      console.log(`Connecting to ${configService.getBackendName()} at ${serverUrl}`);
      
      // Prepare socket configuration with optimized transport settings
      const socketConfig = {
        transports: ['websocket', 'polling'],  // Prefer WebSocket, fallback to polling
        upgrade: true,
        timeout: 30000,
        pingTimeout: 60000,  // Increase ping timeout for stability
        pingInterval: 25000, // Ping every 25 seconds
        forceNew: false,
        reconnection: true,
        reconnectionAttempts: 5,  // Increase reconnection attempts
        reconnectionDelay: 2000,  // Increase delay between attempts
        autoConnect: true,
        withCredentials: !configService.isLocal() // Only for HF backend
      };
      
      console.log('Socket config:', {
        ...socketConfig,
        auth: socketConfig.auth ? { token: '***' } : undefined,
        query: socketConfig.query ? { token: '***' } : undefined,
        extraHeaders: socketConfig.extraHeaders ? { Authorization: '***', 'X-HF-Token': '***' } : undefined
      });
      
      this.socket = io(serverUrl, socketConfig);

      const connectionTimeout = setTimeout(() => {
        console.error('Connection timeout after 30 seconds');
        this.isConnecting = false;
        if (this.socket) {
          this.socket.disconnect();
          this.socket = null;
        }
        reject(new Error('Connection timeout'));
      }, 30000);

      this.socket.on('connect', () => {
        console.log('Successfully connected to server:', serverUrl);
        console.log('Socket ID:', this.socket?.id);
        console.log('Transport:', this.socket?.io.engine.transport.name);
        clearTimeout(connectionTimeout);
        this.isConnecting = false;
        this.connectionAttempts = 0;
        resolve();
      });

      this.socket.on('welcome', (data) => {
        console.log('Received welcome message:', data);
      });

      this.socket.on('connect_error', (error) => {
        console.error('Connection error:', error);
        clearTimeout(connectionTimeout);
        this.isConnecting = false;
        
        if (this.connectionAttempts < this.maxConnectionAttempts) {
          console.log(`Retrying connection in 2 seconds (attempt ${this.connectionAttempts + 1}/${this.maxConnectionAttempts})`);
          setTimeout(() => {
            this.connect(serverUrl).then(resolve).catch(reject);
          }, 2000);
        } else {
          reject(error);
        }
      });

      this.socket.on('disconnect', (reason) => {
        console.log('Disconnected from server:', reason);
        this.isConnecting = false;
      });

      this.socket.on('reconnect', (attemptNumber) => {
        console.log('Reconnected after', attemptNumber, 'attempts');
      });

      this.socket.on('reconnect_error', (error) => {
        console.error('Reconnection failed:', error);
      });
    });
  }

  disconnect() {
    clearTimeout(this._speakingOffTimer);
    this._speakingOffTimer = null;
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.currentSessionId = null;
    this.currentParticipantId = null;
  }

  isConnected() {
    return this.socket?.connected || false;
  }

  isReady() {
    return this.socket !== null && this.socket.connected && !this.isConnecting;
  }

  async joinSession(sessionId, participantName, language) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.socket.connected) {
        reject(new Error('Not connected to server'));
        return;
      }

      console.log('Emitting join_session event:', { sessionId, participantName, language });
      this.currentSessionId = sessionId;
      
      this.socket.off('participant_joined');
      this.socket.off('join_error');
      
      const timeout = setTimeout(() => {
        console.error('Join session timeout - no response from server after 15 seconds');
        this.socket?.off('participant_joined');
        this.socket?.off('join_error');
        reject(new Error('Join session timeout - server did not respond'));
      }, 15000);
      
      try {
        const ttsMode = localStorage.getItem('polyglot-tts-mode') || 'vits';
        this.socket.emit('join_session', {
          sessionId,
          participantName,
          language,
          ttsMode
        });
        console.log('Join session event emitted successfully (ttsMode:', ttsMode, ')');
      } catch (emitError) {
        console.error('Error emitting join_session event:', emitError);
        clearTimeout(timeout);
        reject(new Error(`Failed to emit join request: ${emitError}`));
        return;
      }

      this.socket.once('participant_joined', (participant) => {
        clearTimeout(timeout);
        console.log('Received participant_joined event:', participant);
        this.currentParticipantId = participant.id;
        // Store server hardware config (audioChunkIntervalMs, isGpu, etc.)
        if (participant.serverConfig) {
          this.serverConfig = participant.serverConfig;
          console.log('Server config received:', this.serverConfig);
        }
        resolve(participant);
      });

      this.socket.once('join_error', (error) => {
        clearTimeout(timeout);
        console.error('Received join_error event from server:', error);
        reject(new Error(`Server error: ${error}`));
      });
    });
  }

  sendAudioChunk(audioData, isPauseBoundary = false, audioFormat = null) {
    if (this.socket && this.currentSessionId && this.currentParticipantId) {
      // Send audio as a binary Uint8Array — Socket.IO transmits it as a binary
      // attachment instead of a JSON array, cutting payload size ~5x.
      const payload = {
        sessionId: this.currentSessionId,
        participantId: this.currentParticipantId,
        audioData: audioData instanceof Uint8Array ? audioData : new Uint8Array(audioData),
        isPauseBoundary: isPauseBoundary
      };
      // Include audio format metadata when available so the server never needs to guess
      if (audioFormat) {
        payload.sampleRate = audioFormat.sampleRate;
        payload.channels = audioFormat.channels;
        payload.bitDepth = audioFormat.bitDepth;
      }
      this.socket.emit('audio_chunk', payload);
    }
  }

  /**
   * Send a typed message into the current session. The server treats it like a
   * finished spoken sentence (translations + TTS for every session language).
   * Resolves with the server ack ({ ok, messageId }) or rejects on timeout, which
   * also covers older backends that don't handle `text_message`.
   */
  sendTextMessage(text, clientId = null, timeoutMs = 4000) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.socket.connected || !this.currentSessionId || !this.currentParticipantId) {
        reject(new Error('not_connected'));
        return;
      }
      const timer = setTimeout(() => reject(new Error('ack_timeout')), timeoutMs);
      this.socket.emit('text_message', {
        sessionId: this.currentSessionId,
        participantId: this.currentParticipantId,
        text,
        clientId
      }, (ack) => {
        clearTimeout(timer);
        if (ack && ack.ok) {
          resolve(ack);
        } else {
          reject(new Error(ack?.error || 'rejected'));
        }
      });
    });
  }

  onTypingStart(callback) {
    this.socket?.on('typing_start', callback);
  }

  onTypingStop(callback) {
    this.socket?.on('typing_stop', callback);
  }

  onParticipantLeft(callback) {
    this.socket?.on('participant_left', callback);
  }

  off(event, callback) {
    this.socket?.off(event, callback);
  }

  notifyUserStartedSpeaking() {
    if (this.socket && this.currentSessionId && this.currentParticipantId) {
      this.socket.emit('user_started_speaking', {
        sessionId: this.currentSessionId,
        participantId: this.currentParticipantId
      });
    }
  }

  notifySpeechEnded() {
    if (this.socket && this.currentSessionId && this.currentParticipantId) {
      console.log(`[${new Date().toISOString().slice(11,23)}] Sending speech_ended to server`);
      this.socket.emit('speech_ended', {
        sessionId: this.currentSessionId,
        participantId: this.currentParticipantId
      });
    }
  }

  setSpeakingStatus(isSpeaking) {
    if (!this.socket || !this.currentSessionId || !this.currentParticipantId) return;

    if (isSpeaking) {
      // Rising edge: cancel any pending "stopped" debounce and report immediately
      clearTimeout(this._speakingOffTimer);
      this._speakingOffTimer = null;
      this.socket.emit('speaking_status', {
        sessionId: this.currentSessionId,
        participantId: this.currentParticipantId,
        isSpeaking: true
      });
    } else {
      // Falling edge: debounce 200ms so inter-word volume dips don't trigger
      // the backend's force-finalization mid-sentence.
      // (Reduced from 700ms: on CPU the longer debounce stacked with the throttle
      //  and caused bulk chunk processing after mic stop.)
      if (!this._speakingOffTimer) {
        this._speakingOffTimer = setTimeout(() => {
          this._speakingOffTimer = null;
          if (this.socket && this.currentSessionId && this.currentParticipantId) {
            this.socket.emit('speaking_status', {
              sessionId: this.currentSessionId,
              participantId: this.currentParticipantId,
              isSpeaking: false
            });
          }
        }, 200);
      }
    }
  }

  onSpeakingStatusUpdate(callback) {
    this.socket?.on('speaking_status', callback);
  }

  onTranscriptionUpdate(callback) {
    this.socket?.on('transcription_update', callback);
  }

  onTranscriptionProgress(callback) {
    this.socket?.on('transcription_progress', callback);
  }

  onTranscriptionDebug(callback) {
    this.socket?.on('transcription_debug', callback);
  }

  onTranscriptionComplete(callback) {
    this.socket?.on('transcription_complete', callback);
  }

  onTranslationUpdate(callback) {
    this.socket?.on('translation_update', callback);
  }

  onMessageComplete(callback) {
    this.socket?.on('message_complete', callback);
  }

  onTTSAudio(callback) {
    this.socket?.on('tts_audio', callback);
  }

  onTTSAudioChunk(callback) {
    this.socket?.off('tts_audio_chunk');
    this.socket?.on('tts_audio_chunk', callback);
  }

  onParticipantUpdate(callback) {
    this.socket?.on('participant_update', callback);
  }

  onSessionUpdate(callback) {
    this.socket?.on('session_update', callback);
  }

  removeAllListeners() {
    this.socket?.removeAllListeners();
  }

  getCurrentSessionId() {
    return this.currentSessionId;
  }

  getCurrentParticipantId() {
    return this.currentParticipantId;
  }

  getSocket() {
    return this.socket;
  }

  // Update participant's language (affects speech recognition)
  updateParticipantLanguage(language) {
    if (this.socket && this.currentSessionId && this.currentParticipantId) {
      console.log(`Updating participant language to: ${language}`);
      this.socket.emit('update_participant_language', {
        sessionId: this.currentSessionId,
        participantId: this.currentParticipantId,
        language: language
      });
      return true;
    }
    return false;
  }

  // Update session's target translation language (affects translation output)
  updateTranslationTarget(targetLanguage) {
    if (this.socket && this.currentSessionId) {
      console.log(`Updating translation target to: ${targetLanguage}`);
      this.socket.emit('update_translation_target', {
        sessionId: this.currentSessionId,
        participantId: this.currentParticipantId,
        targetLanguage: targetLanguage
      });
      return true;
    }
    return false;
  }

  // Update session languages (for voice translation mode)
  updateSessionLanguages(languages) {
    if (this.socket && this.currentSessionId) {
      console.log(`Updating session languages to: ${languages}`);
      this.socket.emit('update_session_languages', {
        sessionId: this.currentSessionId,
        languages: languages
      });
      return true;
    }
    return false;
  }
}

export const websocketService = new WebSocketService();
